type YagibuddyFeature = {
  id: string
  emphasis: string
  rest: string
  restPrefix?: string
}

export type YagibuddyCopy = {
  prBadge: string
  closeAriaLabel: string
  titlePrefix: string
  logoYagi: string
  logoBuddy: string
  downloadComplete: string
  subtitle: string
  features: YagibuddyFeature[]
  consentPrefix: string
  privacyLabel: string
  privacyPolicyLabel?: string
  consentSuffix: string
  privacyUrl: string
  ctaLabel: string
  ctaUrlFallback: string
}

function pickStr(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() !== ''
    ? value.trim()
    : fallback
}

function parseFeature(
  value: unknown,
  fallback: YagibuddyFeature
): YagibuddyFeature | null {
  if (!value || typeof value !== 'object') return null
  const row = value as Record<string, unknown>
  const id = pickStr(row.id, fallback.id)
  const emphasis = pickStr(row.emphasis, '')
  if (!id || !emphasis) return null
  const feature: YagibuddyFeature = {
    id,
    emphasis,
    rest: typeof row.rest === 'string' ? row.rest : fallback.rest
  }
  if (typeof row.restPrefix === 'string' && row.restPrefix !== '') {
    feature.restPrefix = row.restPrefix
  } else if (fallback.restPrefix) {
    feature.restPrefix = fallback.restPrefix
  }
  return feature
}

function parseFeatures(
  value: unknown,
  fallback: YagibuddyFeature[]
): YagibuddyFeature[] {
  if (!Array.isArray(value) || value.length === 0) return fallback
  const parsed = value
    .map((item, i) => parseFeature(item, fallback[i] || fallback[0]))
    .filter((item): item is YagibuddyFeature => item != null)
  return parsed.length > 0 ? parsed : fallback
}

/**
 * additional_dialog_info（buildRtDialogInfo 済み）を正本に、FE JSON を欠落時のフォールバックにする。
 */
export function mergeYagibuddyCopy(
  dialogInfo: unknown,
  fallback: YagibuddyCopy
): { copy: YagibuddyCopy; ctaUrl: string } {
  if (!dialogInfo || typeof dialogInfo !== 'object') {
    return { copy: fallback, ctaUrl: fallback.ctaUrlFallback }
  }
  const info = dialogInfo as Record<string, unknown>
  const privacyUrl = pickStr(
    info.privacyUrl,
    pickStr(info.privacyurl, fallback.privacyUrl)
  )
  const copy: YagibuddyCopy = {
    prBadge: pickStr(info.prBadge, fallback.prBadge),
    closeAriaLabel: pickStr(info.closeAriaLabel, fallback.closeAriaLabel),
    titlePrefix: pickStr(info.titlePrefix, fallback.titlePrefix),
    logoYagi: pickStr(info.logoYagi, fallback.logoYagi),
    logoBuddy: pickStr(info.logoBuddy, fallback.logoBuddy),
    downloadComplete: pickStr(info.downloadComplete, fallback.downloadComplete),
    subtitle: pickStr(info.subtitle, fallback.subtitle),
    features: parseFeatures(info.features, fallback.features),
    consentPrefix: pickStr(info.consentPrefix, fallback.consentPrefix),
    privacyLabel: pickStr(info.privacyLabel, fallback.privacyLabel),
    privacyPolicyLabel: pickStr(
      info.privacyPolicyLabel,
      fallback.privacyPolicyLabel || ''
    ),
    consentSuffix: pickStr(info.consentSuffix, fallback.consentSuffix),
    privacyUrl,
    ctaLabel: pickStr(info.ctaLabel, fallback.ctaLabel),
    ctaUrlFallback: fallback.ctaUrlFallback
  }
  return {
    copy,
    ctaUrl: pickStr(info.ctaUrl, fallback.ctaUrlFallback)
  }
}
